import { useMemo, useState } from "react";
import {
  getCustomVoucher,
  getTrackingEvent,
  postTrackingBundle,
} from "../../../../services/offers/offers.service";
import {
  CampaignType,
  DealLayoutSettings,
  MainLayoutSettings,
  Offer,
  OtherSettingsInterface,
  PlacementStage,
  QueryBundle,
  RedeemBy,
  Template,
  TemplateIcons,
  VoucherType,
  LayoutOptions,
  ICONS_DEFAULT_VALUES,
} from "../../../../types/global.types";
import offerImageSrc from "./../../../../assets/images/offer-listening.png";
import PreSelect from "../../components/PreSelect/PreSelect";
import TemplateIcon from "../../components/Icons/info.component";
import BlockedTag from "../../components/BlockedTag/blockedTag.component";
import ImageCarousel from "../../../ImageCarousel/ImageCarousel.component";
interface Props {
  offer: Offer;
  slot: number;
  settings: DealLayoutSettings;
  mainSettings: MainLayoutSettings;
  settingsTemplate: Template;
  displayOnly: boolean;
  isAdded: boolean;
  retailerId: string;
  campaignId: string;
  placementId: string;
  placementName: string;
  sessionId: string;
  campaign: CampaignType;
  openSingle: (
    o: Offer,
    isSave: boolean,
    isAutoEmail: boolean,
    slot: number,
    isShare?: boolean
  ) => void;
  onAdd: (offer: Offer, value: boolean, slot: number) => void;
  hasVoucher: boolean;
  otherSettings: OtherSettingsInterface | undefined;
  selectedEmailOfferId: string;
  setSelectedEmailOfferId: (value: string) => void;
  emailSent: boolean;
  setSelectedEmailOfferTitle: (value: string) => void;
  layoutId: string;
  layoutOptions: LayoutOptions | undefined;
  canAutoSendEmailFromLabel?: boolean;
  canAutoSendEmailFromMainCTA?: boolean;
  onAutoSendEmailFromLabel?: (offer: Offer, slot: number) => void;
  autoSendingOfferId?: boolean;
}

const OfferItem = ({
  offer,
  slot,
  settings,
  campaign,
  settingsTemplate,
  displayOnly,
  retailerId,
  campaignId,
  placementId,
  placementName,
  isAdded,
  openSingle,
  onAdd,
  hasVoucher,
  otherSettings,
  mainSettings,
  selectedEmailOfferId,
  setSelectedEmailOfferId,
  emailSent,
  sessionId,
  setSelectedEmailOfferTitle,
  layoutId,
  layoutOptions,
  canAutoSendEmailFromLabel,
  canAutoSendEmailFromMainCTA,
  onAutoSendEmailFromLabel,
  autoSendingOfferId,
}: Props) => {
  const [copyCode, setCopyCode] = useState<boolean>(false);
  const [getCode, setGetCode] = useState<boolean>(false);
  const [copied, setCopied] = useState(false);
  const [customVoucher, setCustomVoucher] = useState<string>("");
  const [voucherLoading, setVoucherLoading] = useState<boolean>(false);

  const imageUrl = useMemo(() => {
    return offer.media?.mainImage?.length > 0
      ? offer.media.mainImage[0].filepath
      : offer.imageUrl || offerImageSrc;
  }, [offer]);

  const handleAdd = (
    e: React.ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLElement>,
    value: boolean | undefined
  ) => {
    if (displayOnly && !offer.tagBlocked.isBlocked) {
      onAdd(offer, value || false, slot);
      value && handleOpenEmailInput();
    }
  };

  const handleOpenEmailInput = () => {
    if (
      campaign?.campaignGeneralOptions.showAddToOrder &&
      campaign?.campaignGeneralOptions.emailCaptureOnOffers &&
      campaign?.campaignGeneralOptions.emailCaptureOnAddToOrder
    ) {
      openSingle(offer, false, true, slot);
    }
  };

  const handleGetGift = async (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    url: string
  ) => {
    if (offer?.voucher?.voucherType === VoucherType.CUSTOM && !getCode) {
      return;
    }

    if (
      event.target instanceof HTMLElement &&
      !event.target.classList.contains("super-item-info-icon") &&
      !event.target.classList.contains("copy-box") &&
      !event.target.classList.contains("super-email-func") &&
      !displayOnly &&
      offer.offerSettings.redeemBy !== RedeemBy.email
    ) {
      if (offer.offerSettings.bundleOptions.isBundled) {
        const data: QueryBundle = {
          offerId: offer.id,
          campaignId: campaign.id,
          placementId: placementId,
          orderId: offer.orderId,
          userSessionId: sessionId,
          imageUrl: imageUrl,
          pageUrl: window.location.href,
          templateId: settingsTemplate.identifier,
          placementName: placementName,
          pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
          slot: slot,
          layoutId: layoutId,
        };

        const response = await postTrackingBundle(data);

        if (response?.data?.status) {
          window.open(`/bundle-offer/${response?.data?.result}`, "_blank");
        }
      } else {
        window.open(
          url + `&tid=${settingsTemplate.identifier}&slot=${slot}`,
          "_blank"
        );
      }
    }
  };

  const handleClickInfo = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();
    openSingle(offer, false, false, slot);
    getTrackingEvent({
      event: "onreadmoreclick",
      offerId: offer.id,
      retailerId,
      campaignId,
      placementId,
      placementName,
      tid: settingsTemplate.identifier,
      slot,
      pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
      pltid: layoutId,
    });
  };

  const handleClickShare = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();
    openSingle(offer, false, false, slot, true);
  };

  const handleCopyHtml = () => {
    window.focus();
    navigator.clipboard?.writeText(
      customVoucher ? customVoucher : offer.voucher?.code
    );
    setCopied(true);
    setCopyCode(false);
    setTimeout(() => {
      setCopied(false);
    }, 1000); // Show the message for 1 second
  };

  const handleGetCode = async () => {
    setVoucherLoading(true);
    const customVoucher = await getCustomVoucher({
      offerId: offer.id,
      campaignId: campaign.id,
      campaignName: campaign.name,
      companyId: campaign.company_Id,
      placementId: placementId,
      placementName: placementName,
      templateId: settingsTemplate.identifier,
      layoutId: layoutId,
      orderId: offer.orderId,
    });
    setCustomVoucher(customVoucher?.data?.result);
    setGetCode(true);
    setVoucherLoading(false);
  };

  const handleMainCtaAutoSend = () => {
    if (
      canAutoSendEmailFromMainCTA &&
      onAutoSendEmailFromLabel &&
      offer.offerSettings.redeemBy === RedeemBy.webemail &&
      !offer.offerSettings?.bundleOptions?.isBundled &&
      !autoSendingOfferId
    ) {
      onAutoSendEmailFromLabel(offer, slot);
    }
  };

  const getVoucherText = () => {
    return customVoucher ? (
      <>
        <span style={{ fontWeight: "400", marginRight: "3px" }}>Code: </span>

        <span style={{ fontWeight: "600" }}>{customVoucher}</span>
      </>
    ) : offer.voucher?.code ? (
      <>
        <span style={{ fontWeight: "400", marginRight: "3px" }}>Code: </span>
        <span style={{ fontWeight: "600" }}>{offer.voucher?.code}</span>
      </>
    ) : (
      <span style={{ fontWeight: "400" }}>Sorry, code just expired!</span>
    );
  };

  return (
    <>
      <div
        className={`swiper-item ${displayOnly && campaign?.campaignGeneralOptions.showAddToOrder
          ? "display"
          : "getbutton"
          }`}
      >
        <input
          className="hidden-input"
          id={offer.id}
          type="checkbox"
          disabled={
            !displayOnly || !campaign?.campaignGeneralOptions.showAddToOrder
          }
          checked={isAdded}
          onChange={(e) => handleAdd(e, !isAdded)}
        />
        {(layoutOptions?.showBadge ?? true) && (
          <span
            style={{
              position: "absolute",
              top: "-8.5px",
              left: "20px",
              fontSize: "10px",
              textTransform: "uppercase",
              color: offer.badge?.fontColor,
              backgroundColor: offer.badge?.backgroundColor,
              padding: "0 6px",
              borderRadius: "3px",
              fontWeight: "600",
              zIndex: 10,
            }}
            className="badge-settings"
          >
            {offer.badge?.name}
          </span>
        )}
        <label
          htmlFor={offer.id}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              displayOnly
                ? handleAdd(e, !offer.isAdded || false)
                : handleGetGift(e, offer.trackingUrl);
            }
          }}
          className="super-item-layout"
          onClick={(e) => handleGetGift(e, offer.trackingUrl)}
          style={{
            backgroundColor: settings?.backgroundColor,
            fontSize: settings?.fontSize,
            color: settings?.textColor,
            border:
              (selectedEmailOfferId && selectedEmailOfferId === offer.id) ||
                (displayOnly && offer.isAdded)
                ? `1px solid ${mainSettings.highlightColor}`
                : offer.badge?.useBorder && (layoutOptions?.showBadge ?? true)
                  ? `1px solid ${offer.badge?.backgroundColor}`
                  : settings?.borderColor
                    ? `1px solid ${settings?.borderColor}`
                    : "",

            borderRadius: `${mainSettings?.borderRadius}px`,
            position: "relative",
          }}
        >
          {offer.tagBlocked.isBlocked && (
            <BlockedTag tagBlocked={offer.tagBlocked} />
          )}

          <div className="super-item-image">
            {offer.media.mainImage.length > 0 ? (
              <ImageCarousel
                images={offer.media.mainImage.map((image) => image.filepath)}
                showPagination={true}
                borderRadius={0}
                altPrefix="offer"
                style={{ width: "100%", height: "auto" }}
              />
            ) : (
              <img
                className={`image-large`}
                src={imageUrl}
                alt=""
                role="presentation"
                style={{
                  borderRadius: 0,
                }}
              />
            )}
          </div>

          <div
            className={`super-item-content`}
            style={{
              marginBottom: offer.voucher?.voucherType && !campaign?.campaignGeneralOptions.saveOfferOptions.allowSavingOffers ? "20px" : "0px",
            }}
          >
            <div className="super-item-title">
              <span
                className={`super-item-title-text max-2lines `}
                style={{
                  color: settings?.textColor,
                  fontSize: `${Number(settings?.fontSize) + 1}px`,
                }}
                role="contentinfo"
                tabIndex={0}
                aria-label={offer.title}
              >
                {offer.title}
              </span>
            </div>
            <div
              className="super-item-description max-2lines"
              style={{
                color: settings?.textColor,
                fontSize: `${settings?.fontSize}px`,
              }}
              role="contentinfo"
              tabIndex={0}
              aria-label={offer.description}
            >
              {offer.description}
            </div>

            {offer.offerSettings.redeemBy === RedeemBy.email && (
              <>
                {emailSent && (
                  <>
                    {offer.offerSettings.delaySetting.delayValue === "0" ||
                      offer.offerSettings.delaySetting.delayType === "none" ? (
                      <span className="super-item-delay-email">
                        Thank you! We have emailed your offer!
                      </span>
                    ) : (
                      <span className="super-item-delay-email">
                        Thank you! You will receive the offer in{" "}
                        {offer.offerSettings.delaySetting.delayValue}{" "}
                        {offer.offerSettings.delaySetting.delayType}!
                      </span>
                    )}
                  </>
                )}
              </>
            )}

            <div className="super-item-get">
              <div className="super-item-get-center">
                {displayOnly ? (
                  <>
                    {campaign?.campaignGeneralOptions.showAddToOrder && (
                      <PreSelect
                        offerId={offer.id}
                        buttonFontWeight={settings?.buttonFontWeight}
                        buttonFontSize={settings?.buttonFontSize}
                        showCheckboxSelect={otherSettings?.showCheckboxSelect}
                        checked={isAdded}
                        highlightColor={mainSettings?.highlightColor}
                        borderColor={settings?.borderColor}
                        selectedButtonText={otherSettings?.selectedButtonText}
                        notSelectedButtonText={
                          otherSettings?.notSelectedButtonText
                        }
                        textColor={otherSettings?.textColor}
                        settings={otherSettings}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {offer?.voucher?.voucherType === VoucherType.CUSTOM &&
                      !getCode ? (
                      <div
                        className="dashed-button-super copy-box"
                        onClick={handleGetCode}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleGetCode();
                          }
                        }}
                        style={{
                          fontSize: `${settings?.buttonFontSize}px`,
                          position: "relative",
                        }}
                      >
                        {voucherLoading ? (
                          <svg
                            className="spin"
                            viewBox="0 0 1024 1024"
                            focusable="false"
                            data-icon="loading"
                            width="1em"
                            height="1em"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path d="M988 548c-19.9 0-36-16.1-36-36 0-59.4-11.6-117-34.6-171.3a440.45 440.45 0 00-94.3-139.9 437.71 437.71 0 00-139.9-94.3C629 83.6 571.4 72 512 72c-19.9 0-36-16.1-36-36s16.1-36 36-36c69.1 0 136.2 13.5 199.3 40.3C772.3 66 827 103 874 150c47 47 83.9 101.8 109.7 162.7 26.7 63.1 40.2 130.2 40.2 199.3.1 19.9-16 36-35.9 36z"></path>
                          </svg>
                        ) : (
                          "Get code"
                        )}
                      </div>
                    ) : (
                      offer.offerSettings.redeemBy === RedeemBy.webemail && (
                        <div
                          tabIndex={0}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleGetGift(e, offer.trackingUrl);
                            handleMainCtaAutoSend();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.stopPropagation();
                              handleMainCtaAutoSend();
                              handleGetGift(e, offer.trackingUrl);
                            }
                          }}
                          className="super-item-action-get"
                          style={{
                            color: settings?.buttonTextColor,
                            fontSize: `${settings?.buttonFontSize}px`,
                            fontWeight: settings?.buttonFontWeight,
                            backgroundColor: settings?.buttonBackgroundColor,
                            borderRadius: settings?.buttonRounding,
                            border: `1px solid ${settings?.buttonBorderColor ||
                              settings?.buttonBackgroundColor
                              }`,
                            textTransform: settings?.buttonTextTransform,
                          }}
                        >
                          <a
                            href={offer.trackingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            tabIndex={-1}
                          >
                            {offer?.offerCTAText || "Get offer now"}
                          </a>
                        </div>
                      )
                    )}
                  </>
                )}

                <div className="super-item-icons">
                  <div
                    className="super-item-info-icon"
                    onClick={handleClickInfo}
                    tabIndex={0}
                    role="button"
                    aria-label="Read more"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.stopPropagation();
                        handleClickInfo(e);
                      }
                    }}
                  >
                    {!settings?.icons ||
                      settings?.icons === TemplateIcons.default ? (
                      <TemplateIcon
                        icon={
                          settings?.iconInfo?.value ||
                          ICONS_DEFAULT_VALUES.iconInfo
                        }
                        color={settings?.textColor || "#000"}
                      />
                    ) : (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M12 0C5.37 0 0 5.37 0 12C0 18.63 5.37 24 12 24C18.63 24 24 18.63 24 12C24 5.37 18.63 0 12 0ZM13.5 18.5C13.5 19.33 12.83 20 12 20C11.17 20 10.5 19.33 10.5 18.5V10.5C10.5 9.67 11.17 9 12 9C12.83 9 13.5 9.67 13.5 10.5V18.5ZM12 7C11.17 7 10.5 6.33 10.5 5.5C10.5 4.67 11.17 4 12 4C12.83 4 13.5 4.67 13.5 5.5C13.5 6.33 12.83 7 12 7Z"
                          fill={settings?.textColor}
                        />
                      </svg>
                    )}
                  </div>

                  {!displayOnly &&
                    campaign.sharingOptions?.allowSharing &&
                    campaign.sharingOptions.sharingPlatforms?.platforms
                      ?.length > 0 &&
                    offer?.offerSettings?.allowShare &&
                    (layoutOptions?.showShare ?? true) && (
                      <div
                        className="super-item-share-icon super-item-info-icon"
                        onClick={handleClickShare}
                        tabIndex={0}
                        role="button"
                        aria-label="Share"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleClickShare(e);
                          }
                        }}
                      >
                        <TemplateIcon
                          icon={
                            settings?.iconShare?.value ||
                            ICONS_DEFAULT_VALUES.iconShare
                          }
                          color={settings?.textColor || "#000"}
                        />
                      </div>
                    )}
                </div>
              </div>
            </div>


          </div>

          {!displayOnly &&
            campaign.campaignGeneralOptions.saveOfferOptions
              .allowSavingOffers &&
            !offer.offerSettings?.bundleOptions?.isBundled &&
            campaign.campaignGeneralOptions.saveOfferOptions.allowEmail &&
            (layoutOptions?.showEmail ?? true) && (
              <div
                className={`super-item-email-label super-email-func`}
                onClick={() => {
                  if (
                    canAutoSendEmailFromLabel &&
                    !!onAutoSendEmailFromLabel &&
                    offer.offerSettings.redeemBy === RedeemBy.webemail
                  ) {
                    onAutoSendEmailFromLabel(offer, slot);
                    return;
                  }
                  setSelectedEmailOfferTitle(offer.title);
                  setSelectedEmailOfferId(offer.id);
                }}
                tabIndex={0}
                role="button"
                aria-label="Add to email"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (
                      canAutoSendEmailFromLabel &&
                      onAutoSendEmailFromLabel &&
                      offer.offerSettings.redeemBy === RedeemBy.webemail
                    ) {
                      onAutoSendEmailFromLabel(offer, slot);
                      return;
                    }
                    setSelectedEmailOfferTitle(offer.title);
                    setSelectedEmailOfferId(offer.id);
                  }
                }}
                style={{
                  backgroundColor: `${settings?.emailBackground}`,
                  border: `1px solid ${settings?.emailBorderColor}`,
                  borderRadius: `${settings?.emailRounding}px`,
                  marginBottom: offer.voucher?.voucherType ? "20px" : "0",
                }}
              >
                <>
                  <span
                    className="super-item-email-icon super-email-func"
                    style={{ backgroundColor: settings?.backgroundColor }}
                  >
                    {!settings?.icons ||
                      settings?.icons === TemplateIcons.default ? (
                      <TemplateIcon
                        icon={
                          settings?.iconEmail?.value ||
                          ICONS_DEFAULT_VALUES.iconEmail
                        }
                        color={settings?.emailIconColor || "#888888"}
                      />
                    ) : (
                      <svg
                        width="32"
                        height="24"
                        viewBox="0 0 32 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ marginLeft: 5 }}
                      >
                        <path
                          d="M21 4.00024H3C2.45 4.00024 2 4.45024 2 5.00024V19.0002C2 19.5502 2.45 20.0002 3 20.0002H21C21.55 20.0002 22 19.5502 22 19.0002V5.00024C22 4.45024 21.55 4.00024 21 4.00024ZM20.38 5.00024L12 13.0802L3.69 5.00024H20.38ZM3 19.0002V5.72024L12 14.4702L21 5.80024V19.0002H3Z"
                          fill={settings?.emailIconColor || "#888888"}
                        />
                      </svg>
                    )}
                  </span>
                  <span
                    className="super-email-func"
                    style={{
                      color: settings?.emailTextColor || "#888888",
                      fontSize: `${settings?.emailFontSize}px`,
                      fontWeight: settings?.emailFontWeight || "400",
                    }}
                  >
                    {autoSendingOfferId
                      ? "Sending email..."
                      :
                      settings?.emailMeTheGift || "Email me the gift"}
                  </span>
                </>
              </div>
            )}

          {offer.voucher?.voucherType && (
            <span
              style={{
                position: "absolute",
                bottom: "0",
                left: "0",
                fontSize: "11px",
                color: settings?.buttonTextColor,
                backgroundColor: settings?.buttonBackgroundColor,
                borderRadius: `0 0 ${mainSettings?.borderRadius}px ${mainSettings?.borderRadius}px`,
                fontWeight: "600",
                textAlign: "center",
                width: "100%",
                height: "20px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              onMouseEnter={() => setCopyCode(true)}
              onMouseLeave={() => {
                setCopyCode(false);
              }}
              onClick={() => handleCopyHtml()}
              tabIndex={0}
              role="button"
              aria-label="Copy code"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleCopyHtml();
                }
              }}
            >
              {offer.voucher?.voucherType === VoucherType.CUSTOM && !getCode ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                  }}
                  className="copy-box"
                >
                  Code:
                  <svg
                    style={{
                      marginLeft: "5px",
                    }}
                    width="70"
                    height="16"
                    viewBox="0 0 70 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="70" height="16" fill="" />
                    <circle
                      cx="4"
                      cy="8"
                      r="2"
                      fill={settings?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="10"
                      cy="8"
                      r="2"
                      fill={settings?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="52"
                      cy="8"
                      r="2"
                      fill={settings?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="58"
                      cy="8"
                      r="2"
                      fill={settings?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="16"
                      cy="8"
                      r="2"
                      fill={settings?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="22"
                      cy="8"
                      r="2"
                      fill={settings?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="28"
                      cy="8"
                      r="2"
                      fill={settings?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="34"
                      cy="8"
                      r="2"
                      fill={settings?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="40"
                      cy="8"
                      r="2"
                      fill={settings?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="46"
                      cy="8"
                      r="2"
                      fill={settings?.buttonTextColor || "white"}
                    />
                  </svg>
                </div>
              ) : (
                <>
                  {copyCode ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                        fontWeight: "400",
                      }}
                      onClick={handleCopyHtml}
                      tabIndex={0}
                      role="button"
                      aria-label="Copy code"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleCopyHtml();
                        }
                      }}
                      className="copy-box"
                    >
                      {copied ? (
                        <>
                          <svg
                            width="8"
                            height="6"
                            viewBox="0 0 8 6"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{
                              marginRight: "5px",
                            }}
                          >
                            <path
                              d="M1 3L3 5L7 1"
                              stroke={settings?.buttonTextColor || "white"}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Copied successfully
                        </>
                      ) : (
                        <>
                          <svg
                            width="11"
                            height="10"
                            viewBox="0 0 11 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{
                              marginRight: "5px",
                            }}
                          >
                            <path
                              d="M7.75 3V2C7.75 1.73478 7.63147 1.48043 7.42049 1.29289C7.20952 1.10536 6.92337 1 6.625 1H2.125C1.82663 1 1.54048 1.10536 1.3295 1.29289C1.11853 1.48043 1 1.73478 1 2V6C1 6.26522 1.11853 6.51957 1.3295 6.70711C1.54048 6.89464 1.82663 7 2.125 7H3.25M4.375 3H8.875C9.49632 3 10 3.44772 10 4V8C10 8.55228 9.49632 9 8.875 9H4.375C3.75368 9 3.25 8.55228 3.25 8V4C3.25 3.44772 3.75368 3 4.375 3Z"
                              stroke={settings?.buttonTextColor || "white"}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Copy Code
                        </>
                      )}
                    </div>
                  ) : copied ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        fontWeight: "400",
                      }}
                      className="copy-box"
                      role="button"
                      aria-label="Copy code"
                    >
                      <svg
                        width="8"
                        height="6"
                        viewBox="0 0 8 6"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          marginRight: "5px",
                        }}
                      >
                        <path
                          d="M1 3L3 5L7 1"
                          stroke={settings?.buttonTextColor || "white"}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Copied successfully
                    </div>
                  ) : (
                    getVoucherText()
                  )}
                </>
              )}
            </span>
          )}
        </label>
      </div>
    </>
  );
};

export default OfferItem;
